import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { isHttpError } from '@/api/custom-client';
import { usersProfileRetrieve } from '@/api/hanly-v2/users/users';
import { baseURL } from '@/libs/api';
import { AuthContext } from '../contexts/auth-context';

const RESTRICTED_PATHS = ['/signin'];

interface LoginCredentials {
  username: string;
  password: string;
}

interface TokenResponse {
  access: string;
  refresh: string;
}

const UserSchema = z.object({
  id: z.number(),
  username: z.string(),
  role: z.string(),
  name: z.string(),
});

const AuthInfoSchema = z.object({
  user: z.nullable(UserSchema),
  isAuthenticated: z.boolean(),
  // 接続元ごとの回数の上限(429)でログイン状態を確かめられなかった。トークンは残っているので未ログインとは扱わない
  isRateLimited: z.boolean().optional(),
});
type AuthInfo = z.infer<typeof AuthInfoSchema>;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthCheck = () => {
  return useSuspenseQuery({
    queryKey: ['auth'],
    queryFn: async (): Promise<AuthInfo> => {
      if (!localStorage.getItem('accessToken')) {
        return { user: null, isAuthenticated: false };
      }

      try {
        const data = await usersProfileRetrieve();
        return {
          user: data,
          isAuthenticated: true,
        };
      } catch (error) {
        if (isHttpError(error) && error.status === 429) {
          return { user: null, isAuthenticated: false, isRateLimited: true };
        }
        console.error(error);
        return { user: null, isAuthenticated: false };
      }
    },
    refetchOnWindowFocus: false,
    retry: false,
  });
};

/** ログインの応答。MFA が有効なユーザーはトークンの代わりにコード入力を求められる */
export type SigninResult =
  | { status: 'signed_in' }
  | {
      status: 'mfa_required';
      // 認証アプリのコードと一緒に送り返す短命トークン
      mfaToken: string;
    };

interface MfaSigninCredentials {
  mfaToken: string;
  mfaCode: string;
}

const MFA_REQUIRED_CODE = 'MFA_REQUIRED';

/** 認証自体は成功したが、ログイン後のプロフィール取得に失敗した */
export class ProfileLoadError extends Error {
  constructor() {
    super('Failed to load profile');
    this.name = 'ProfileLoadError';
  }
}

/** 短時間にログインを繰り返し、接続元ごとの回数の上限(429)に達した */
export class SigninRateLimitedError extends Error {
  constructor() {
    super('Too many sign-in attempts');
    this.name = 'SigninRateLimitedError';
  }
}

/** パスワードログインが失敗した理由。呼び出し側の表示の出し分けに使う */
export type SigninErrorReason = 'invalid_credentials' | 'profile_failed' | 'rate_limited';

const toSigninErrorReason = (error: unknown): SigninErrorReason => {
  if (error instanceof ProfileLoadError) return 'profile_failed';
  if (error instanceof SigninRateLimitedError) return 'rate_limited';
  return 'invalid_credentials';
};

/**
 * 取得したトークンを保存し、プロフィールを読み込んでアプリ内へ遷移する。
 * パスワードのみのログインと MFA コード入力後のログインで共通。
 */
const useCompleteSignin = ({
  onSuccess: handleSuccess,
  redirectTo,
}: {
  onSuccess?: () => void;
  redirectTo: string;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  return async (data: TokenResponse) => {
    localStorage.setItem('accessToken', data.access);
    localStorage.setItem('refreshToken', data.refresh);

    const profileData = await usersProfileRetrieve()
      .then((res) => res)
      .catch(() => undefined);
    if (profileData === undefined) {
      console.error('failed profile data access');
      // トークンだけ残ると「保存済みだが未認証」の半端な状態になるため破棄し、呼び出し側へ失敗を伝える
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      throw new ProfileLoadError();
    }
    queryClient.setQueryData(['auth'], { user: profileData, isAuthenticated: true });
    authContext?.setAuthInfo?.((prev) => ({
      ...prev,
      isAuthenticated: true,
    }));

    handleSuccess?.();

    const targetPath = RESTRICTED_PATHS.includes(redirectTo) ? '/' : redirectTo;
    navigate(targetPath);
  };
};

export const useSignin = ({
  onSuccess: handleSuccess,
  onMfaRequired: handleMfaRequired,
  onError: handleError,
  redirectTo = '/',
}: {
  onSuccess?: () => void;
  // MFA が有効なユーザー。呼び出し側はコード入力に切り替え、useMfaSignin へ mfaToken を渡す
  onMfaRequired?: (mfaToken: string) => void;
  onError?: (reason: SigninErrorReason) => void;
  redirectTo?: string;
}) => {
  const completeSignin = useCompleteSignin({ onSuccess: handleSuccess, redirectTo });

  return useMutation({
    mutationFn: async (credentials: LoginCredentials): Promise<SigninResult> => {
      // 認証前なので、401 でトークンリフレッシュを試みる customInstance は使わない
      const response = await fetch(`${baseURL}/api/v2/accounts/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      // MFA が必要な場合は 401 だが認証情報自体は正しいので、エラーではなくコード入力へ進める
      if (response.status === 401) {
        const data = (await response.json().catch(() => null)) as {
          code?: string;
          mfa_token?: string;
        } | null;
        if (data?.code === MFA_REQUIRED_CODE && typeof data.mfa_token === 'string') {
          return { status: 'mfa_required', mfaToken: data.mfa_token };
        }
        throw new Error('Authentication failed');
      }

      if (response.status === 429) {
        throw new SigninRateLimitedError();
      }
      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      await completeSignin((await response.json()) as TokenResponse);
      return { status: 'signed_in' };
    },
    onSuccess: (result) => {
      if (result.status === 'mfa_required') {
        handleMfaRequired?.(result.mfaToken);
      }
    },
    onError: (error) => {
      handleError?.(toSigninErrorReason(error));
    },
  });
};

/** MFA コード入力が失敗した理由。呼び出し側の表示の出し分けに使う */
export type MfaSigninErrorReason =
  // コード誤り・ロック中など。message は API の文言
  | { reason: 'invalid_code'; message: string }
  // mfa_token の期限切れ等。メールアドレスとパスワードの入力からやり直す
  | { reason: 'expired' }
  // 短時間にコードの送信を繰り返し、接続元ごとの回数の上限(429)に達した
  | { reason: 'rate_limited' }
  | { reason: 'unknown' };

export class MfaSigninError extends Error {
  constructor(public detail: MfaSigninErrorReason) {
    super('MFA sign-in failed');
    this.name = 'MfaSigninError';
  }
}

interface StandardizedErrorLike {
  errors?: { attr?: string | null; detail?: string }[];
}

/**
 * ログインが MFA を要求した後、認証アプリのコード(またはバックアップコード)でログインを完了する
 */
export const useMfaSignin = ({
  onSuccess: handleSuccess,
  onError: handleError,
  redirectTo = '/',
}: {
  onSuccess?: () => void;
  onError?: (detail: MfaSigninErrorReason) => void;
  redirectTo?: string;
}) => {
  const completeSignin = useCompleteSignin({ onSuccess: handleSuccess, redirectTo });

  return useMutation({
    mutationFn: async (credentials: MfaSigninCredentials): Promise<void> => {
      // 認証前なので、401 でトークンリフレッシュを試みる customInstance は使わない
      const response = await fetch(`${baseURL}/api/v2/accounts/token/mfa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mfa_token: credentials.mfaToken, mfa_code: credentials.mfaCode }),
      });

      if (response.status === 400) {
        const data = (await response.json().catch(() => null)) as StandardizedErrorLike | null;
        const codeError = data?.errors?.find((e) => e.attr === 'mfa_code');
        throw new MfaSigninError({
          reason: 'invalid_code',
          message: codeError?.detail ?? 'コードが正しくありません',
        });
      }
      if (response.status === 401) {
        throw new MfaSigninError({ reason: 'expired' });
      }
      if (response.status === 429) {
        throw new MfaSigninError({ reason: 'rate_limited' });
      }
      if (!response.ok) {
        throw new MfaSigninError({ reason: 'unknown' });
      }

      await completeSignin((await response.json()) as TokenResponse);
    },
    onError: (error) => {
      handleError?.(error instanceof MfaSigninError ? error.detail : { reason: 'unknown' });
    },
  });
};

export const useSignout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.setQueryData(['auth'], { isAuthenticated: false });
      queryClient.clear();
      toast.success('ログアウトしました');
      navigate('/signin');
    },
  });
};
